import {
  DEFAULT_RIBBON_STYLE,
  IHomeTileConfig,
  IHomeTileConfigSaveBody,
  IHomeTileConfigUpdateBody,
  IPersonaRuleNode,
  IRibbonStyle,
} from "@/api_framework/api_modals/HomeTileConfig";
import { toWireStatus } from "@/app/hooks/api_hooks/quick_commerce/useHomeTileConfigAPI";
import {
  DEFAULT_BASIC_PERSONA_LOGIC,
  IHomeTileConfigFormFields,
  RIBBON_TEXT_CONTENT_PATTERN,
  RIBBON_TEXT_MAX_LENGTH,
  RIBBON_TEXT_MIN_LENGTH,
  getRibbonTextLength,
} from "../interface";
import { fromPersonaRule, validatePersonaCombination } from "../personaExpression";
import { toBasicPersonaLogic } from "./basicPersonaLogic";

export const DEFAULT_VALUES: IHomeTileConfigFormFields = {
  tile_id: "",
  persona_logic_mode: "BASIC",
  basic_persona: { ...DEFAULT_BASIC_PERSONA_LOGIC },
  persona_combination: "",
  ribbon_text: "",
  ribbon: { ...DEFAULT_RIBBON_STYLE },
  status: "ACTIVE",
  overrides_dynamic: false,
};

export const hasStaticRibbon = (values: IHomeTileConfigFormFields): boolean =>
  Boolean(values.persona_combination.trim() || values.ribbon_text.trim());

export const toRuleKey = (rule: IPersonaRuleNode | null): string =>
  rule ? (validatePersonaCombination(fromPersonaRule(rule)).canonical ?? "") : "";

export const validateRibbonTextValue = (value: string): true | string => {
  const trimmed = value.trim();
  if (!trimmed) return "Ribbon text is required.";

  const length = getRibbonTextLength(trimmed);
  if (length < RIBBON_TEXT_MIN_LENGTH) return `Ribbon text must be at least ${RIBBON_TEXT_MIN_LENGTH} characters.`;
  if (length > RIBBON_TEXT_MAX_LENGTH) return `Ribbon text must be ${RIBBON_TEXT_MAX_LENGTH} characters or less.`;
  return RIBBON_TEXT_CONTENT_PATTERN.test(trimmed) || "Ribbon text must include at least one letter, number or emoji.";
};

export const buildSaveBody = (
  data: IHomeTileConfigFormFields,
  rule: IPersonaRuleNode,
  isServiceTile: boolean,
): IHomeTileConfigSaveBody => ({
  tile_id: data.tile_id,
  configuration: { persona_rule: rule },
  ribbon_text: data.ribbon_text.trim(),
  ribbon: { ...data.ribbon },
  status: toWireStatus(data.status),
  ...(isServiceTile && { overrides_dynamic: data.overrides_dynamic }),
});

export const buildChanges = (body: IHomeTileConfigSaveBody, saved: IHomeTileConfig): IHomeTileConfigUpdateBody => {
  const changes: IHomeTileConfigUpdateBody = {};

  if (body.ribbon_text !== saved.ribbon_text) changes.ribbon_text = body.ribbon_text;
  if (body.status !== toWireStatus(saved.status)) changes.status = body.status;
  if (body.overrides_dynamic !== undefined && body.overrides_dynamic !== saved.overrides_dynamic) {
    changes.overrides_dynamic = body.overrides_dynamic;
  }

  const hasRibbonChanged = (Object.keys(body.ribbon) as (keyof IRibbonStyle)[]).some(
    (key) => body.ribbon[key] !== saved.ribbon[key],
  );
  if (hasRibbonChanged) changes.ribbon = body.ribbon;

  if (toRuleKey(body.configuration.persona_rule ?? null) !== toRuleKey(saved.persona_rule)) {
    changes.configuration = body.configuration;
  }

  return changes;
};

export const toFormValues = (config: IHomeTileConfig): IHomeTileConfigFormFields => {
  const basic = toBasicPersonaLogic(config.persona_rule);
  return {
    tile_id: config.tile_id,
    persona_logic_mode: basic ? "BASIC" : "ADVANCED",
    basic_persona: basic ?? { ...DEFAULT_BASIC_PERSONA_LOGIC },
    persona_combination: fromPersonaRule(config.persona_rule),
    ribbon_text: config.ribbon_text,
    ribbon: { ...config.ribbon },
    status: config.status,
    overrides_dynamic: config.overrides_dynamic,
  };
};
